import { FormEvent, useEffect, useState, type CSSProperties } from "react";
// `formatDate` je ovde nekad stajao kao sopstvena funkcija bez provere, pa je prazan datum davao
// „Invalid Date" na ekranu. Deljena iz `lib/format` prazno i neispravno vraća kao prazno.
import { formatDate, formatDateTime, formatMoneyCode, formatNumber } from "../../lib/format";
import { PanelLoading } from "../../components/Loading";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  deliveriesApi,
  displayFinding,
  nextAllowedDelivery,
  type Delivery,
  type DeliveryStatus,
  type QualityFindingStatus,
  type QualityValueChange,
  type QualityValueEntry,
  type SettlementProposal,
  type DeliverySettlement,
} from "../../api/deliveries";
import { procurementContractsApi, type ProcurementContract } from "../../api/procurementContracts";
import { tripsApi, type TripView } from "../../api/transport";
import type { ApiError } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../components/Toast";
import { useConfirm } from "../../components/ConfirmDialog";
import { DeliveryStatusBadge } from "../../components/DeliveryStatusBadge";
import { DeliveryNoContractTag } from "../../components/DeliveryNoContractTag";
import Ic from "../../components/icons.jsx";
import { DeliveryDocuments } from "../../components/DeliveryDocuments";
import { ExternalAnalysesPanel } from "../../components/ExternalAnalyses";
import { ExportButton } from "../../components/ExportButton";
import { qualityApi, type QualityParameter } from "../../api/quality";
import { qualityClassesApi, criterionText, type QualityClass } from "../../api/qualityClasses";
import { localizedName } from "../../lib/localizedName";
import { Select } from "../../components/Select";

/** Happy-path order for the workflow stepper — mirrors OrderEditorPage's
 * ol.wf section. The two side-exits are not steps on it: REJECTED is entered
 * through the laboratory's finding, CANCELLED by withdrawing the intake order
 * while the truck is still expected. */
const WF_STEPS: DeliveryStatus[] = ["ANNOUNCED", "CONFIRMED", "SAMPLED", "RECEIVED", "SETTLED"];

/**
 * Isporuke i kvalitet detail — the inbound counterpart of OrderEditorPage.
 * Three hard-separated action gates:
 * - deliveries:announce (OTKUP / ADMIN) — the list-level "Announce delivery"
 *   form, and here both the transport correction and the withdrawal of the
 *   intake order, each only while the truck is still expected.
 * - deliveries:confirm (OTKUP) — confirm / weighbridge receipt / settlement / reject.
 *   A delivery with no contract in force behind it is confirmed by a director
 *   (ADMIN) alone.
 * - deliveries:quality (KVALITET) — the lab analysis.
 * The first two also need the delivery's `canAct`: the desk reads every
 * supplier's deliveries but acts only on its assigned suppliers'.
 * The supplier holds none of these: it reads its delivery and attaches its note.
 *
 * The backend's DeliveryView carries no status-history list (unlike Order) and
 * there is no dedicated history endpoint, even though DeliveryStatusHistory
 * rows are written on every transition server-side. The "Status timeline"
 * section below is therefore a derived stepper (current status + which data
 * fields are populated), not a fetched chronological event log.
 */
export default function DeliveryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const { hasPermission, state } = useAuth();

  const [delivery, setDelivery] = useState<Delivery | null>(null);
  const [contract, setContract] = useState<ProcurementContract | null>(null);
  // Until the linked contract has been looked up, whether it is in force is unknown.
  const [contractResolved, setContractResolved] = useState(false);
  const [trips, setTrips] = useState<TripView[]>([]);
  const [busy, setBusy] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);

  const canViewTrips = hasPermission("trips:view");
  // The correction trail is the lab's working record: Bankom's staff read it, the supplier does not.
  const isStaff = state.status === "authenticated" && state.user.internal;
  const [corrections, setCorrections] = useState<QualityValueChange[]>([]);
  const [erpSettlement, setErpSettlement] = useState<DeliverySettlement[]>([]);
  const [erpLoading, setErpLoading] = useState(false);

  // Receipt form
  const [grossKg, setGrossKg] = useState("");
  const [tareKg, setTareKg] = useState("");
  const [dispatchNoteQtyKg, setDispatchNoteQtyKg] = useState("");
  // Transport correction form, prefilled from the delivery once it loads
  const [carrierName, setCarrierName] = useState("");
  const [vehiclePlate, setVehiclePlate] = useState("");
  const [trailerPlate, setTrailerPlate] = useState("");
  const [driverName, setDriverName] = useState("");
  // Settlement form
  const [priceCorrectionPct, setPriceCorrectionPct] = useState("");
  const [settledAmount, setSettledAmount] = useState("");
  // Quality form
  const [parameters, setParameters] = useState<QualityParameter[]>([]);
  const [ticketNo, setTicketNo] = useState("");
  const [gateIn, setGateIn] = useState("");
  const [gateOut, setGateOut] = useState("");
  const [proposal, setProposal] = useState<SettlementProposal | null>(null);
  const [proposalError, setProposalError] = useState<string | null>(null);
  const [manualPrice, setManualPrice] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [qualityInput, setQualityInput] = useState<Record<string, string>>({});
  // A correction states what happened and why the new finding is right. Kept per
  // parameter, because one save may correct one finding and add another.
  const [correctionReason, setCorrectionReason] = useState<Record<string, string>>({});
  const [correctionExplanation, setCorrectionExplanation] = useState<Record<string, string>>({});
  // Reject form
  const [rejectReason, setRejectReason] = useState("");
  const [showExceptionForm, setShowExceptionForm] = useState(false);
  // The classes of the crop being delivered. Loaded for the lab; a failure simply hides
  // the class card rather than the page.
  const [classes, setClasses] = useState<QualityClass[]>([]);
  const [provisionalChoice, setProvisionalChoice] = useState("");
  const [finalChoice, setFinalChoice] = useState("");
  const [classChangeReason, setClassChangeReason] = useState("");
  const [exceptionReason, setExceptionReason] = useState("");
  // Withdrawal form. The reason is the whole record of why the desk took its order back.
  const [cancelReason, setCancelReason] = useState("");

  // The desk's gates need both the permission and the server's word that this
  // delivery's supplier is the caller's to act on.
  const mayAct = delivery?.canAct === true;
  // A receipt imported from the ERP is settled there, on the final incoming invoice, and its findings
  // are those of its lot. The server refuses the settlement, the finding and the class on it.
  const fromErp = delivery?.origin === "ERP";
  const canConfirm = hasPermission("deliveries:confirm") && mayAct;
  // Local settlement is retired: the directory exposes ERP settlement Read only.
  const canSettle = isStaff && hasPermission("section:supplier_settlements:full") && canConfirm && !fromErp;
  const canQuality = hasPermission("deliveries:quality");
  const canVerifyQuality = hasPermission("deliveries:quality_verify");
  const canReadExternal = hasPermission("section:external_analyses:read");
  // Three permissions, because the refusal is two steps and two roles. The screen shows
  // only the half this caller holds; the server decides either way.
  const canLabReject = hasPermission("deliveries:lab_reject");
  const canConfirmRejection = hasPermission("deliveries:reject_confirm") && mayAct;
  const canWaiveRejection = hasPermission("deliveries:reject_exception");
  const canAnnounce = hasPermission("deliveries:announce") && mayAct;
  // Mirror of the server-side rule: a delivery with no contract in force behind
  // it is confirmed by a director alone.

  async function load() {
    if (!id) return;
    try {
      const d = await deliveriesApi.get(id);
      setDelivery(d);
      setCarrierName(d.carrierName ?? "");
      setVehiclePlate(d.vehiclePlate ?? "");
      setTrailerPlate(d.trailerPlate ?? "");
      setDriverName(d.driverName ?? "");
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when the route id changes
  useEffect(() => { void load(); }, [id]);

  // Read separately from the intake rather than along with it: a page of intakes would otherwise
  // carry every line of every settling invoice, and the settlement is read one intake at a time.
  async function loadErpSettlement() {
    if (!id || !hasPermission("section:supplier_settlements:read")) return;
    setErpLoading(true);
    try {
      setErpSettlement(await deliveriesApi.erpSettlement(id));
    } catch {
      setErpSettlement([]);
    } finally {
      setErpLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when the route id changes
  useEffect(() => { void loadErpSettlement(); }, [id]);

  async function loadCorrections() {
    if (!id || !isStaff || !hasPermission("section:delivery_quality:read")) return;
    try {
      setCorrections(await deliveriesApi.qualityChanges(id));
    } catch {
      setCorrections([]);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when the route id changes
  useEffect(() => { void loadCorrections(); }, [id, isStaff]);

  // Trip link: GET /transport/trips?deliveryId=
  // resolves "which trip is this delivery on" as a single targeted backend
  // lookup (see TripService#list's javadoc), rather than loading every trip
  // and searching them here.
  // Best-effort like the label lookups elsewhere in this module: a caller
  // without trips:view never reaches this at all, since the row it feeds is
  // gated on the same permission.
  useEffect(() => {
    if (!canViewTrips || !delivery) { setTrips([]); return; }
    void tripsApi.list({ deliveryId: delivery.id }).then(setTrips).catch(() => setTrips([]));
  }, [canViewTrips, delivery?.id]);

  // The intake form is built from the codebook, so it must be loaded before the
  // lab can type anything into it.
  // The proposal is what the configured rules make of the measured quality. It
  // is fetched, never computed here — the arithmetic lives in one place.
  useEffect(() => {
    if (!canSettle || !id || delivery?.status !== "RECEIVED") { setProposal(null); setProposalError(null); return; }
    const price = manualPrice.trim() === "" ? undefined : Number(manualPrice);
    deliveriesApi
      .settlementProposal(id, price)
      .then((p) => {
        setProposal(p);
        setProposalError(null);
        setPriceCorrectionPct(String(p.correctionPct));
        setSettledAmount(String(p.amount));
      })
      .catch((e: ApiError) => {
        setProposal(null);
        setProposalError(e.message);
      });
  }, [canSettle, id, delivery?.status, manualPrice]);

  useEffect(() => {
    if (!canQuality) return;
    qualityApi
      .listParameters(true)
      .then(setParameters)
      .catch(() => setParameters([]));
  }, [canQuality]);

  // Best-effort: resolve the linked contract's reference number for display.
  // Never blocks the page if the caller can't read it.
  useEffect(() => {
    if (!delivery?.procurementContractId) { setContract(null); setContractResolved(true); return; }
    let live = true;
    setContractResolved(false);
    void procurementContractsApi.get(delivery.procurementContractId)
      .then((c) => { if (live) setContract(c); })
      .catch(() => { if (live) setContract(null); })
      .finally(() => { if (live) setContractResolved(true); });
    return () => { live = false; };
  }, [delivery?.procurementContractId]);

  useEffect(() => {
    if (!canQuality) return;
    const culture = contract?.culture;
    void qualityClassesApi
      .list(culture ? { culture } : {})
      .then(setClasses)
      .catch(() => undefined);
  }, [canQuality, contract?.culture]);

  if (!delivery) {
    return <PanelLoading text={t("common.loading")} />;
  }

  // The laboratory's forms, the finding and the class: while the load is sampled or in, and only on a
  // delivery opened in the portal.
  const labMayEnter = canQuality && !fromErp && (delivery.status === "SAMPLED" || delivery.status === "RECEIVED");
  // Where the sentence on an imported receipt points: its settlement further down this page, once the
  // ERP has invoiced it, and the findings of its lot.
  const erpSettlementShown =
    hasPermission("section:supplier_settlements:read") && !erpLoading && erpSettlement.length > 0;
  // Until 2018 the ERP did not record which incoming invoice paid for a receipt. An imported receipt
  // from those years with no invoice is therefore not waiting for a settlement: whether it had one is
  // not known.
  const settlementUnknownBefore2018 = fromErp
    && delivery.status === "RECEIVED"
    && delivery.receivedOn != null
    && delivery.receivedOn < "2018-01-01"
    && !erpLoading
    && erpSettlement.length === 0;
  const lotFindingsLink = delivery.goodsLotId != null
    && hasPermission("section:intake_lots:read") && hasPermission("section:lab_archive:read");

  // A contract that is not ACTIVE, or cannot be read, counts as none. While it
  // is still being looked up, the button waits rather than guessing.
  const contractPending = !!delivery.procurementContractId && !contractResolved;
  const contractInForce = contract?.status === "ACTIVE";
  const confirmNeedsDirector = !hasPermission("deliveries:approve_without_contract") && !contractPending && !contractInForce;
  // The gate knows the truck by its plate: no plate, no driver, no confirmation.
  const transportMissing = !delivery.vehiclePlate || !delivery.driverName;
  const transportMissingHint = t("deliveries.hint.confirmNeedsTransport",
      "Record the vehicle plate and the driver under Transport details before confirming.");
  const confirmDirectorHint = !delivery.procurementContractId
    ? t("deliveries.hint.confirmWithoutContract", "This delivery runs outside any procurement contract. Such a delivery needs the commercial director's prior approval, so a director confirms it.")
    : t("deliveries.hint.confirmContractNotInForce", "The procurement contract behind this delivery is not in force, so a director confirms it.");
  // A changed amount needs a reason. The server enforces this too; the form just
  // stops the user from finding out the hard way.
  const deviates = proposal != null && Math.abs(Number(settledAmount) - proposal.amount) > 0.01;
  // The trip this delivery rides on, if any — and singular here is a guarantee,
  // not an accident: `uq_trip_item_delivery` is a partial unique index over live
  // trip items, so one delivery physically cannot sit on two trips at once. That
  // is the opposite of the outbound side, where an order MAY split across two
  // lorries and the order screen therefore lists them all.
  const trip = trips[0] ?? null;
  /**
   * Withdrawing the intake order, gated on exactly what the server gates it on: the announce
   * key, a supplier this desk acts on, no weighing yet, and a state the withdrawal is still
   * allowed from. `transportEditable` already carries three of those four — the server sets it
   * to `canAct && the gate has not weighed the truck` — but it is one state too wide: a plate may
   * still be corrected once the sample is taken, while from there every outcome speaks about the
   * goods and there is no paperwork left to take back. The permitted states therefore come from
   * the transition table, which mirrors the server's own.
   */
  const mayCancel =
    canAnnounce && delivery.transportEditable && nextAllowedDelivery(delivery.status).includes("CANCELLED");

  async function doConfirm() {
    if (!delivery) return;
    setBusy(true);
    try {
      await deliveriesApi.confirm(delivery.id);
      await load();
      toast.success(t("deliveries.toast.confirmed", "Delivery confirmed."));
    } catch (err) {
      const e = err as ApiError;
      toast.error(deliveryErrorMessage(e, t));
    } finally {
      setBusy(false);
    }
  }

  /**
   * The desk takes its own intake order back. Administrative and nothing more, so it asks for the
   * reason first and the confirmation second; an empty reason never leaves the screen.
   */
  async function doCancel(e: FormEvent) {
    e.preventDefault();
    if (!delivery) return;
    const reason = cancelReason.trim();
    if (!reason) {
      toast.error(t("deliveries.error.cancelReasonRequired",
          "An announcement is withdrawn with a reason, never without one."));
      return;
    }
    if (!(await confirm({
      message: t("deliveries.confirm.cancel",
          "Withdraw this announcement? The intake order stays on record as withdrawn."),
    }))) return;
    setBusy(true);
    try {
      await deliveriesApi.cancel(delivery.id, reason);
      await load();
      setCancelReason("");
      toast.success(t("deliveries.toast.cancelled", "The announcement has been withdrawn."));
    } catch (err) {
      toast.error(deliveryErrorMessage(err as ApiError, t));
    } finally {
      setBusy(false);
    }
  }

  /** The class decided before unloading: it is what sends the lorry to a silo. */
  async function assignProvisionalClass() {
    if (!delivery || !provisionalChoice) return;
    setBusy(true);
    try {
      await deliveriesApi.assignProvisionalClass(delivery.id, provisionalChoice);
      await load();
      toast.success(t("deliveries.toast.provisionalClass", "Provisional class assigned."));
    } catch (err) {
      toast.error(deliveryErrorMessage(err as ApiError, t));
    } finally {
      setBusy(false);
    }
  }

  /** The class once every result is in. A reason is required when it differs. */
  async function assignFinalClass() {
    if (!delivery || !finalChoice) return;
    const differs = delivery.provisionalClassId != null && delivery.provisionalClassId !== finalChoice;
    if (differs && !classChangeReason.trim()) {
      toast.error(t(
        "deliveries.error.classChangeReasonRequired",
        "The final class differs from the provisional one — say why the load changed class.",
      ));
      return;
    }
    setBusy(true);
    try {
      await deliveriesApi.assignFinalClass(delivery.id, finalChoice, differs ? classChangeReason.trim() : null);
      await load();
      setClassChangeReason("");
      toast.success(t("deliveries.toast.finalClass", "Final class assigned."));
    } catch (err) {
      toast.error(deliveryErrorMessage(err as ApiError, t));
    } finally {
      setBusy(false);
    }
  }

  async function doSample() {
    if (!delivery) return;
    setBusy(true);
    try {
      await deliveriesApi.sample(delivery.id);
      await load();
      toast.success(t("deliveries.toast.sampled", "Sample recorded; the analysis can be entered."));
    } catch (err) {
      toast.error(deliveryErrorMessage(err as ApiError, t));
    } finally {
      setBusy(false);
    }
  }

  async function submitTransport(e: FormEvent) {
    e.preventDefault();
    if (!delivery) return;
    setBusy(true);
    try {
      await deliveriesApi.updateTransport(delivery.id, {
        carrierName: carrierName.trim(),
        vehiclePlate: vehiclePlate.trim(),
        trailerPlate: trailerPlate.trim(),
        driverName: driverName.trim(),
      });
      await load();
      toast.success(t("deliveries.toast.transportSaved", "Transport details saved."));
    } catch (err) {
      toast.error(deliveryErrorMessage(err as ApiError, t));
    } finally {
      setBusy(false);
    }
  }

  async function submitReceipt(e: FormEvent) {
    e.preventDefault();
    if (!delivery) return;
    const gross = Number(grossKg);
    const tare = Number(tareKg);
    if (!(gross >= 0) || !(tare >= 0)) {
      toast.error(t("deliveries.error.weightsRequired", "Gross and tare weight are required."));
      return;
    }
    const dispatchQty = dispatchNoteQtyKg.trim() === "" ? null : Number(dispatchNoteQtyKg);
    if (dispatchQty != null && !(dispatchQty >= 0)) {
      toast.error(t("deliveries.error.dispatchNoteQtyInvalid", "The dispatch note quantity must be a number ≥ 0."));
      return;
    }
    setBusy(true);
    try {
      await deliveriesApi.recordReceipt(delivery.id, {
        grossKg: gross,
        tareKg: tare,
        dispatchNoteQtyKg: dispatchQty,
        weighbridgeTicketNo: ticketNo.trim() || null,
        gateInAt: gateIn ? new Date(gateIn).toISOString() : null,
        gateOutAt: gateOut ? new Date(gateOut).toISOString() : null,
      });
      await load();
      setGrossKg(""); setTareKg(""); setDispatchNoteQtyKg("");
      toast.success(t("deliveries.toast.receiptSaved", "Weighbridge receipt saved."));
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  async function submitQuality(e: FormEvent) {
    e.preventDefault();
    if (!delivery) return;
    // Only what the lab actually filled in is sent; a blank field means "not
    // measured", not zero or no. The shape follows the parameter: a figure for a
    // numeric test, a yes/no for GMO, words for an observation.
    const values: QualityValueEntry[] = [];
    for (const p of parameters) {
      const raw = qualityInput[p.id];
      if (raw == null || raw.trim() === "") continue;
      const type = p.resultType ?? "NUMERIC";
      // A correction of a finding already on the delivery states both the reason and
      // the explanation; the server refuses it without them.
      const existing = delivery.qualityValues.find((q) => q.parameterId === p.id);
      const correcting = existing != null;
      const reason = correcting ? (correctionReason[p.id] ?? "").trim() : "";
      const explanation = correcting ? (correctionExplanation[p.id] ?? "").trim() : "";
      if (correcting && (!reason || !explanation)) {
        toast.error(t(
          "deliveries.error.correctionReasonRequired",
          "Correcting a recorded finding needs both a reason and an explanation.",
        ));
        return;
      }
      if (type === "NUMERIC") {
        const n = Number(raw);
        if (Number.isNaN(n)) {
          toast.error(t("deliveries.error.qualityNumber", "Quality values must be numbers."));
          return;
        }
        values.push({ parameterId: p.id, value: n, reason: reason || null, explanation: explanation || null });
      } else if (type === "BOOLEAN") {
        values.push({
          parameterId: p.id,
          valueBool: raw === "true",
          reason: reason || null,
          explanation: explanation || null,
        });
      } else {
        values.push({
          parameterId: p.id,
          valueText: raw.trim(),
          reason: reason || null,
          explanation: explanation || null,
        });
      }
    }
    if (values.length === 0) {
      toast.error(t("deliveries.error.qualityRequired", "Enter at least one measured value."));
      return;
    }
    setBusy(true);
    try {
      await deliveriesApi.recordQuality(delivery.id, { values });
      await load();
      await loadCorrections();
      setCorrectionReason({});
      setCorrectionExplanation({});
      toast.success(t("deliveries.toast.qualitySaved", "Quality analysis saved."));
    } catch (err) {
      toast.error(deliveryErrorMessage(err as ApiError, t));
    } finally {
      setBusy(false);
    }
  }

  /** The head of the laboratory signs one finding. */
  async function verifyFinding(parameterId: string) {
    if (!delivery) return;
    setBusy(true);
    try {
      await deliveriesApi.verifyQualityValue(delivery.id, parameterId);
      await load();
      toast.success(t("deliveries.toast.findingVerified", "Finding verified."));
    } catch (err) {
      toast.error(deliveryErrorMessage(err as ApiError, t));
    } finally {
      setBusy(false);
    }
  }

  async function submitSettle(e: FormEvent) {
    e.preventDefault();
    if (!delivery || !canSettle) return;
    const pct = Number(priceCorrectionPct);
    const amount = Number(settledAmount);
    if (Number.isNaN(pct) || Number.isNaN(amount)) {
      toast.error(t("deliveries.error.settlementRequired", "Price correction and settled amount are required."));
      return;
    }
    if (!(await confirm({ message: t("deliveries.confirm.settle", "Settle this delivery? This is the final step and cannot be undone.") }))) return;
    setBusy(true);
    try {
      await deliveriesApi.settle(delivery.id, {
        priceCorrectionPct: pct,
        settledAmount: amount,
        overrideReason: overrideReason.trim() || null,
        manualPricePerT: manualPrice.trim() === "" ? null : Number(manualPrice),
      });
      await load();
      toast.success(t("deliveries.toast.settled", "Delivery settled."));
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  /** Step one: the laboratory records that the load is non-conforming and stops there. */
  async function submitLabReject(e: FormEvent) {
    e.preventDefault();
    if (!delivery) return;
    if (!rejectReason.trim()) {
      toast.error(t("deliveries.error.rejectReasonRequired", "Say what the laboratory found."));
      return;
    }
    setBusy(true);
    try {
      await deliveriesApi.labReject(delivery.id, rejectReason.trim());
      await load();
      setShowRejectForm(false);
      setRejectReason("");
      toast.success(t(
        "deliveries.toast.labRejected",
        "Finding recorded. The buy-in desk confirms the refusal, or a director waives it.",
      ));
    } catch (err) {
      toast.error(deliveryErrorMessage(err as ApiError, t));
    } finally {
      setBusy(false);
    }
  }

  /** Step two: the desk confirms the refusal, the supplier is told, the register gets a row. */
  async function confirmRejection() {
    if (!delivery) return;
    if (!(await confirm({
      message: t(
        "deliveries.confirm.rejectConfirm",
        "Confirm the refusal and inform the supplier? This cannot be undone.",
      ),
      danger: true,
    }))) return;
    setBusy(true);
    try {
      await deliveriesApi.confirmRejection(delivery.id);
      await load();
      toast.success(t("deliveries.toast.rejected", "Refusal confirmed; the supplier has been informed."));
    } catch (err) {
      toast.error(deliveryErrorMessage(err as ApiError, t));
    } finally {
      setBusy(false);
    }
  }

  /** The other outcome: the managing director accepts the load in spite of the finding. */
  async function submitRejectException(e: FormEvent) {
    e.preventDefault();
    if (!delivery) return;
    if (!exceptionReason.trim()) {
      toast.error(t(
        "deliveries.error.rejectExceptionReasonRequired",
        "Say why the load is accepted in spite of the finding.",
      ));
      return;
    }
    setBusy(true);
    try {
      await deliveriesApi.approveRejectionException(delivery.id, exceptionReason.trim());
      await load();
      setShowExceptionForm(false);
      setExceptionReason("");
      toast.success(t("deliveries.toast.rejectException", "Waiver recorded; the load returns to the weighbridge."));
    } catch (err) {
      toast.error(deliveryErrorMessage(err as ApiError, t));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <nav className="crumbs" aria-label={t("deliveryDetail.breadcrumb.aria", "Breadcrumb")}>
            <span style={crumbMuted}>{t("nav.group.otkup", "Otkup")}</span>
            <span><Link to="/procurement/deliveries" style={crumbLink}>{t("deliveries.list.title", "Deliveries")}</Link></span>
            <span style={crumbCurrent}>
              {/* Uvezen prijem nema najavu, pa se u putanji vidi dan prijema. */}
              {formatDate(delivery.receivedOn ?? delivery.announcedDate ?? "", i18n.resolvedLanguage) || "—"}
            </span>
          </nav>
          <h1>{delivery.companyName}</h1>
          <div className="sub">
            {delivery.announcedDate
              ? t("deliveries.detail.announcedOn", "announced {{date}}", {
                  date: formatDate(delivery.announcedDate, i18n.resolvedLanguage),
                })
              : delivery.receivedOn
                ? t("deliveries.detail.receivedOn", "primljeno {{date}}", {
                    date: formatDate(delivery.receivedOn, i18n.resolvedLanguage),
                  })
                : ""}
          </div>
        </div>
        <div className="page-actions">
          <button type="button" onClick={() => navigate(-1)} className="btn">
            {t("orderEditor.back", "Back")}
          </button>
        </div>
      </div>

      <div style={kpiStrip}>
        <div style={kpiTile}>
          <span style={kpiLabel}>{t("deliveries.kpi.status", "Status")}</span>
          <strong style={kpiValueLg}><DeliveryStatusBadge status={delivery.status} /></strong>
        </div>
        <div style={kpiTile}>
          <span style={kpiLabel}>{t("deliveries.kpi.estimatedQuantity", "Est. quantity")}</span>
          <strong style={kpiValueLg}>
            {delivery.estimatedQuantityT != null ? (
              `${delivery.estimatedQuantityT} t`
            ) : (
              <span style={{ color: "var(--ink-3)", fontWeight: 400 }}>—</span>
            )}
          </strong>
        </div>
        <div style={kpiTile}>
          <span style={kpiLabel}>{t("deliveries.kpi.netKg", "Net weight")}</span>
          <strong style={kpiValueLg}>{delivery.netKg != null ? `${delivery.netKg} kg` : <span style={{ color: "var(--ink-3)", fontWeight: 400 }}>—</span>}</strong>
        </div>
        <div style={kpiTile}>
          <span style={kpiLabel}>{t("deliveries.kpi.contract", "Contract")}</span>
          <strong style={kpiValueLg}>
            {contract
              ? contract.referenceNumber
              : delivery.procurementContractId
                ? <span style={{ color: "var(--ink-3)", fontWeight: 400 }}>—</span>
                : <DeliveryNoContractTag delivery={delivery} />}
          </strong>
        </div>
      </div>

      <div className="order-layout">
        <section style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <section style={card}>
            <h2 style={cardTitle}>{t("deliveries.section.announcement", "Announcement")}</h2>
            <Grid>
              <ReadField
                label={t("deliveries.field.announcedDate", "Announced date")}
                value={delivery.announcedDate ? formatDate(delivery.announcedDate, i18n.resolvedLanguage) : "—"}
              />
              {/* Dan prijema stoji uz najavu a ne umesto nje: uvezen kamion ima samo njega. */}
              <ReadField
                label={t("deliveries.field.receivedOn", "Primljeno")}
                value={delivery.receivedOn ? formatDate(delivery.receivedOn, i18n.resolvedLanguage) : "—"}
              />
              <ReadField
                label={t("deliveries.field.estimatedQuantity", "Estimated quantity (t)")}
                value={delivery.estimatedQuantityT != null ? String(delivery.estimatedQuantityT) : "—"}
              />
              <ReadField label={t("deliveries.field.contract", "Procurement contract")} value={contract?.referenceNumber ?? "—"} />
            </Grid>
          </section>

          {/* The withdrawal, read straight after the announcement it belongs to. An ordinary card
              and not a red one: taking back an order nobody needs any more is housekeeping, not a
              finding against the goods. The supplier reads it here too — it is the answer to „what
              happened to my slot". */}
          {delivery.cancelledAt && (
            <section style={card}>
              <h2 style={cardTitle}>{t("deliveries.section.cancelled", "Withdrawn announcement")}</h2>
              <p style={{ margin: 0, fontSize: 13, color: "var(--ink-2)" }}>
                {delivery.cancelReason || t("deliveries.field.noReason", "No reason recorded.")}
              </p>
              <span className="muted" style={{ fontSize: 11.5 }}>
                {t("deliveries.cancel.when", "Withdrawn {{when}}", { when: formatDateTime(delivery.cancelledAt) })}
              </span>
            </section>
          )}

          {(delivery.grossKg != null || delivery.tareKg != null) && (
            <section style={card}>
              <h2 style={cardTitle}>{t("deliveries.section.receipt", "Weighbridge receipt")}</h2>
              <Grid>
                <ReadField label={t("deliveries.field.grossKg", "Gross (kg)")} value={fmt(delivery.grossKg)} />
                <ReadField label={t("deliveries.field.tareKg", "Tare (kg)")} value={fmt(delivery.tareKg)} />
                <ReadField label={t("deliveries.field.netKg", "Net (kg)")} value={fmt(delivery.netKg)} />
                <ReadField
                  label={t("deliveries.field.dispatchNoteQtyKg", "Quantity per supplier dispatch note (kg)")}
                  value={fmt(delivery.dispatchNoteQtyKg)}
                />
              </Grid>
            </section>
          )}

          {(delivery.carrierName || delivery.vehiclePlate || delivery.weighbridgeTicketNo) && (
            <section style={card}>
              <h2 style={cardTitle}>{t("deliveries.section.transport", "Transport")}</h2>
              <Grid>
                <ReadField label={t("deliveries.transport.carrier", "Carrier")} value={delivery.carrierName ?? "—"} />
                <ReadField label={t("deliveries.transport.vehiclePlate", "Vehicle plate")} value={delivery.vehiclePlate ?? "—"} mono />
                <ReadField label={t("deliveries.transport.trailerPlate", "Trailer plate")} value={delivery.trailerPlate ?? "—"} mono />
                <ReadField label={t("deliveries.transport.driver", "Driver")} value={delivery.driverName ?? "—"} />
                <ReadField label={t("deliveries.transport.ticketNo", "Weighbridge ticket")} value={delivery.weighbridgeTicketNo ?? "—"} mono />
                <ReadField
                  label={t("deliveries.transport.gateIn", "Gate in")}
                  value={delivery.gateInAt ? new Date(delivery.gateInAt).toLocaleString(i18n.resolvedLanguage) : "—"}
                />
                <ReadField
                  label={t("deliveries.transport.gateOut", "Gate out")}
                  value={delivery.gateOutAt ? new Date(delivery.gateOutAt).toLocaleString(i18n.resolvedLanguage) : "—"}
                />
              </Grid>
            </section>
          )}

          {/* The trip this delivery rides on, if any. Not rendered
              at all when the delivery isn't on a trip — an empty row would
              be noise, not information. */}
          {trip && (
            <section style={card}>
              <h2 style={cardTitle}>{t("deliveries.section.trip", "Trip")}</h2>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span style={fieldLabel}>{t("trips.col.tripNumber", "Trip number")}</span>
                <Link to={`/transport/trips/${trip.id}`} style={{ fontSize: 13, color: "var(--brand-primary)" }}>
                  <code>{trip.tripNumber}</code>
                </Link>
              </div>
            </section>
          )}

          {delivery.qualityValues.length > 0 && (
            <section style={card}>
              <h2 style={cardTitle}>{t("deliveries.section.quality", "Quality analysis")}</h2>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {delivery.qualityValues.map((q) => (
                  <div
                    key={q.parameterId}
                    style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}
                  >
                    <span style={{ ...fieldLabel, minWidth: 150 }}>
                      {localizedName({ name: q.parameterKey, nameI18n: q.nameI18n }, i18n.resolvedLanguage ?? "sr")}
                      {q.unit ? ` (${q.unit})` : ""}
                    </span>
                    <span style={{ fontSize: 13, color: "var(--ink)", fontWeight: 600 }}>
                      {displayFinding(q)}
                    </span>
                    <FindingLimitTag outOfRange={q.outOfRange} />
                    <FindingStatusTag status={q.status} verifiedByName={q.verifiedByName} />
                    {canVerifyQuality && q.status === "PENDING_VERIFICATION" && (
                      <button type="button" className="btn sm" disabled={busy} onClick={() => void verifyFinding(q.parameterId)}>
                        {t("deliveries.action.verifyFinding", "Verify")}
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {corrections.length > 0 && (
                <div style={{ borderTop: "1px solid var(--border)", paddingTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                  <span style={{ fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4 }}>
                    {t("deliveries.quality.corrections", "Corrections")}
                  </span>
                  <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
                    {corrections.map((c, i) => (
                      <li key={`${c.parameterId}-${c.changedAt}-${i}`} style={{ fontSize: 13, color: "var(--ink-2)" }}>
                        <b style={{ color: "var(--ink)" }}>
                          {localizedName({ name: c.parameterKey ?? "", nameI18n: c.nameI18n }, i18n.resolvedLanguage ?? "sr")}
                        </b>
                        {": "}
                        <span style={{ textDecoration: "line-through" }}>{c.previousValue}</span>
                        {" → "}
                        {c.newValue}
                        {c.unit ? ` ${c.unit}` : ""}
                        {c.reason && (
                          <div style={{ fontSize: 12, color: "var(--ink-2)" }}>
                            {c.reason}
                            {c.explanation ? ` — ${c.explanation}` : ""}
                          </div>
                        )}
                        <div style={{ fontSize: 11.5, color: "var(--ink-3)" }}>
                          {t("deliveries.quality.correctedBy", "Corrected by {{name}}, {{when}}", {
                            name: c.changedByName ?? "—",
                            when: formatDateTime(c.changedAt),
                          })}
                          {" · "}
                          {t("deliveries.quality.enteredBy", "entered by {{name}}, {{when}}", {
                            name: c.previousRecordedByName ?? "—",
                            when: formatDateTime(c.previousRecordedAt),
                          })}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          )}

          {/* Outside findings sit next to the lab's own: whoever reads the analysis of this load has to
              see what an outside laboratory measured on it as well. Shown to the roles that may read
              them, so a supplier is never sent a request that comes back refused. */}
          {canReadExternal && <ExternalAnalysesPanel subject={{ kind: "delivery", id: delivery.id }} />}

          {hasPermission("section:supplier_settlements:read") && delivery.settledAmount != null && (
            <section style={card}>
              <h2 style={cardTitle}>{t("deliveries.section.settlement", "Settlement")}</h2>
              <Grid>
                <ReadField label={t("deliveries.field.priceCorrectionPct", "Price correction (%)")} value={fmt(delivery.priceCorrectionPct)} />
                <ReadField label={t("deliveries.field.settledAmount", "Settled amount")} value={fmt(delivery.settledAmount)} />
              </Grid>
            </section>
          )}

          {/* Bankom settles an intake with no document of its own: the settlement is the incoming
              invoice, whose dispatch-note field names the receipts it pays for. So this is an invoice
              the portal reads, not an amount the portal computes. */}
          {hasPermission("section:supplier_settlements:read") && erpLoading && <PanelLoading text={t("deliveries.section.erpSettlement", "Settlement in the ERP")} />}
          {hasPermission("section:supplier_settlements:read") && settlementUnknownBefore2018 && (
            <section style={card}>
              <h2 style={cardTitle}>{t("deliveries.section.erpSettlement", "Settlement in the ERP")}</h2>
              <p style={{ margin: 0, fontSize: 13, color: "var(--ink-2)" }}>
                {t("deliveries.settlement.unknownBefore2018", "Settlement unknown (before 2018)")}
              </p>
              <p style={settlementNote}>
                {t("deliveries.settlement.unknownBefore2018Note",
                   "Until 2018 the ERP did not record which incoming invoice pays for a receipt, so whether and when this receipt was settled is not known.")}
              </p>
            </section>
          )}
          {hasPermission("section:supplier_settlements:read") && !erpLoading && erpSettlement.map((s, i) => (
            <section
              key={s.invoiceId}
              id={i === 0 ? "erp-settlement" : undefined}
              style={{ ...card, scrollMarginTop: "calc(var(--topbar-h) + 16px)" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <h2 style={cardTitle}>{t("deliveries.section.erpSettlement", "Settlement in the ERP")}</h2>
                {s.partial && <span className="tag warn">{t("deliveries.settlement.partial", "partial")}</span>}
              </div>
              <Grid>
                <ReadField
                  label={t("deliveries.field.settlementInvoice", "Invoice")}
                  value={s.invoiceNumber || "—"}
                  mono
                />
                <ReadField
                  label={t("deliveries.field.settlementIssued", "Invoice date")}
                  value={formatDate(s.issueDate)}
                />
                <ReadField
                  label={t("deliveries.field.settlementCovers", "Receipts on the invoice")}
                  value={String(s.coveredReceipts)}
                />
                {s.amountForThisDelivery != null && (
                  <ReadField
                    label={t("deliveries.field.settlementShare", "For this intake")}
                    value={formatMoneyCode(s.amountForThisDelivery, s.currency)}
                  />
                )}
              </Grid>
              <table className="tbl" style={{ fontSize: 12 }}>
                <thead>
                  <tr>
                    <th>{t("deliveries.settlement.product", "Article")}</th>
                    <th style={{ textAlign: "right" }}>{t("deliveries.settlement.quantity", "Invoiced")}</th>
                    <th style={{ textAlign: "right" }}>{t("deliveries.settlement.unitPrice", "Price")}</th>
                    <th style={{ textAlign: "right" }}>{t("deliveries.settlement.netAmount", "Amount")}</th>
                  </tr>
                </thead>
                <tbody>
                  {s.lines.map((l, i) => (
                    <tr key={i}>
                      <td>{l.productName || "—"}</td>
                      <td style={{ textAlign: "right" }}>
                        {formatNumber(l.quantity)}{l.unit ? ` ${l.unit}` : ""}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {formatMoneyCode(l.unitPrice, s.currency, undefined, { maximumFractionDigits: 4 })}
                      </td>
                      <td style={{ textAlign: "right" }}>{formatMoneyCode(l.netAmount, s.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* One invoice covers a loading run and not one truck — up to twenty receipts. Without
                  this line the amount beside a single intake would read as that intake's. */}
              {/* An invoice that pays for part of this intake does not say which part: no share is
                  stated, and the note says why instead of the one about several articles. */}
              {s.partial ? (
                <p style={settlementNote}>
                  {t("deliveries.settlement.partialNote",
                     "The invoice pays for only part of this receipt; another invoice that names it too pays for the rest. The invoice does not say which part, so no amount is attributed to this receipt.")}
                </p>
              ) : (
                <>
                  {s.coveredReceipts > 1 && (
                    <p style={settlementNote}>
                      {t("deliveries.settlement.coversMany",
                         "The amount is the whole invoice's and covers {{n}} receipts; “For this intake” is its share by weighed mass.",
                         { n: s.coveredReceipts })}
                    </p>
                  )}
                  {s.amountForThisDelivery == null && (
                    <p style={settlementNote}>
                      {t("deliveries.settlement.manyArticles",
                         "The invoice carries several articles, so no amount is attributed to this intake.")}
                    </p>
                  )}
                </>
              )}
            </section>
          ))}

          {/* The class decides the silo and the permitted use, so the card shows both
              rather than just a label. Assignment is the laboratory's; the read-out is
              for everyone who may see the delivery. */}
          {(delivery.provisionalClassId || delivery.finalClassId || labMayEnter) && (
            <section style={card}>
              <h2 style={cardTitle}>{t("deliveries.section.class", "Raw-material class")}</h2>
              <DeliveryClassReadout
                classes={classes}
                provisionalClassId={delivery.provisionalClassId}
                finalClassId={delivery.finalClassId}
                classChangeReason={delivery.classChangeReason}
              />
              {labMayEnter && (
                classes.length === 0 ? (
                  <p className="muted" style={{ margin: 0, fontSize: 12 }}>
                    {t(
                      "deliveries.class.noneDefined",
                      "No classes are defined for this crop, so there is nothing to assign yet.",
                    )}
                  </p>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <Field label={t("deliveries.class.provisional", "Provisional class (picks the silo)")}>
                      <div style={{ display: "flex", gap: 8 }}>
                        <Select<string>
                          value={provisionalChoice}
                          onChange={setProvisionalChoice}
                          ariaLabel={t("deliveries.class.provisional", "Provisional class")}
                          placeholder={t("deliveries.class.pick", "— Pick a class —")}
                          style={{ flex: 1 }}
                          options={classes.map((c) => ({
                            value: c.id,
                            label: `${c.code} — ${localizedName({ name: c.code, nameI18n: c.nameI18n }, i18n.resolvedLanguage ?? "sr")}`,
                          }))}
                        />
                        <button
                          type="button"
                          className="btn sm"
                          disabled={busy || !provisionalChoice}
                          onClick={() => void assignProvisionalClass()}
                        >
                          {t("action.save", "Save")}
                        </button>
                      </div>
                    </Field>
                    <Field label={t("deliveries.class.final", "Final class (decides the permitted use)")}>
                      <div style={{ display: "flex", gap: 8 }}>
                        <Select<string>
                          value={finalChoice}
                          onChange={setFinalChoice}
                          ariaLabel={t("deliveries.class.final", "Final class")}
                          placeholder={t("deliveries.class.pick", "— Pick a class —")}
                          style={{ flex: 1 }}
                          options={classes.map((c) => ({
                            value: c.id,
                            label: `${c.code} — ${localizedName({ name: c.code, nameI18n: c.nameI18n }, i18n.resolvedLanguage ?? "sr")}`,
                          }))}
                        />
                        <button
                          type="button"
                          className="btn sm"
                          disabled={busy || !finalChoice}
                          onClick={() => void assignFinalClass()}
                        >
                          {t("action.save", "Save")}
                        </button>
                      </div>
                    </Field>
                    {finalChoice && delivery.provisionalClassId && finalChoice !== delivery.provisionalClassId && (
                      <Field label={t("deliveries.class.changeReason", "Why the load changed class")} required>
                        <input
                          className="input"
                          value={classChangeReason}
                          onChange={(e) => setClassChangeReason(e.target.value)}
                          style={{ width: "100%" }}
                        />
                      </Field>
                    )}
                    <span className="muted" style={{ fontSize: 11.5 }}>
                      {t(
                        "deliveries.class.hint",
                        "The provisional class comes from the fast parameters, because the lorry cannot wait for oil and protein. The final one comes when every result is in; if it differs, the goods went into the silo of another class and the reason is the only record of it.",
                      )}
                    </span>
                  </div>
                )
              )}
            </section>
          )}

          {(delivery.labRejectedAt || delivery.rejectExceptionAt) && (
            <section style={card}>
              <h2 style={cardTitle}>{t("deliveries.section.refusalTrail", "Refusal trail")}</h2>
              <Grid>
                {delivery.labRejectReason && (
                  <ReadField
                    label={t("deliveries.field.labRejectReason", "What was found")}
                    value={delivery.labRejectReason}
                  />
                )}
                {delivery.rejectConfirmedAt && (
                  <ReadField
                    label={t("deliveries.field.rejectConfirmedAt", "Refusal confirmed")}
                    value={formatDateTime(delivery.rejectConfirmedAt)}
                  />
                )}
                {delivery.rejectExceptionReason && (
                  <ReadField
                    label={t("deliveries.field.rejectExceptionReason", "Accepted anyway, because")}
                    value={delivery.rejectExceptionReason}
                  />
                )}
              </Grid>
            </section>
          )}

          {delivery.status === "REJECTED" && (
            <section style={{ ...card, borderColor: "var(--err)" }}>
              <h2 style={{ ...cardTitle, color: "var(--err)" }}>{t("deliveries.section.reject", "Rejection")}</h2>
              <p style={{ margin: 0, fontSize: 13, color: "var(--ink-2)" }}>
                {delivery.rejectReason || t("deliveries.field.noReason", "No reason recorded.")}
              </p>
            </section>
          )}
        </section>

        <aside style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <section style={card}>
            <h2 style={cardTitle}>{t("deliveries.timeline.title", "Status timeline")}</h2>
            <WorkflowStepper delivery={delivery} settlementUnknown={settlementUnknownBefore2018} />
            <p style={{ margin: 0, fontSize: 12, color: "var(--ink-3)" }}>
              {fromErp
                ? t("deliveries.timeline.noteErp", "This receipt was brought over from the ERP, and only the state it was found in is shown. It was never announced, confirmed or sampled in the portal.")
                : t("deliveries.timeline.note", "A delivery moves Announced, Confirmed, Sampled, Received, Settled. The sample comes before the weighbridge: only a load the figures allow is weighed and unloaded.")}
            </p>
          </section>

          {/* On a receipt imported from the ERP this sentence stands where a delivery opened here
              carries the settlement and the laboratory's forms. */}
          {fromErp && (
            <section style={card}>
              <h2 style={cardTitle}>{t("deliveries.section.fromErp", "Receipt from the ERP")}</h2>
              <p style={{ margin: 0, fontSize: 13, color: "var(--ink-2)" }}>
                {t(
                  "deliveries.fromErp.note",
                  "The settlement is the final incoming invoice in the ERP, and the laboratory records its findings by lot, not by receipt.",
                )}
              </p>
              {(erpSettlementShown || lotFindingsLink) && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 12, fontSize: 13 }}>
                  {erpSettlementShown && (
                    // Scrolled to rather than followed: a followed fragment is one more history step,
                    // and Back on this page would then land on this same page.
                    <a
                      href="#erp-settlement"
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById("erp-settlement")?.scrollIntoView({ behavior: "smooth", block: "start" });
                      }}
                      style={{ color: "var(--brand-primary)" }}
                    >
                      {t("deliveries.section.erpSettlement", "Settlement in the ERP")}
                    </a>
                  )}
                  {lotFindingsLink && (
                    <Link to={`/procurement/lots/${delivery.goodsLotId}`} style={{ color: "var(--brand-primary)" }}>
                      {t("deliveries.fromErp.lotFindings", "Lot findings")}
                    </Link>
                  )}
                </div>
              )}
            </section>
          )}

          {canConfirm && delivery.status === "ANNOUNCED" && (
            <section style={{ ...card, borderColor: "var(--brand-primary)", boxShadow: "0 0 0 3px var(--brand-primary-50)" }}>
              <h2 style={cardTitle}>{t("deliveries.section.otkupActions", "Otkup actions")}</h2>
              <p style={{ margin: 0, fontSize: 12, color: "var(--ink-3)" }}>
                {transportMissing
                  ? transportMissingHint
                  : confirmNeedsDirector
                    ? confirmDirectorHint
                    : t("deliveries.hint.confirm", "Confirm the delivery slot to let the supplier know it's expected.")}
              </p>
              <button
                type="button"
                className="btn primary"
                onClick={() => void doConfirm()}
                disabled={busy || contractPending || confirmNeedsDirector || transportMissing}
                title={transportMissing
                  ? transportMissingHint
                  : confirmNeedsDirector
                    ? (delivery.procurementContractId
                        ? confirmDirectorHint
                        : t("deliveries.contract.noneHint", "Outside any procurement contract — a director confirms it."))
                    : undefined}
              >
                <Ic.check /> {t("deliveries.action.confirm", "Confirm")}
              </button>
            </section>
          )}

          {canAnnounce && delivery.transportEditable && (
            <section style={card}>
              <h2 style={cardTitle}>{t("deliveries.action.editTransport", "Transport details")}</h2>
              <p style={{ margin: 0, fontSize: 12, color: "var(--ink-3)" }}>
                {t("deliveries.hint.editTransport", "The plate and the driver may still be corrected; once the truck is weighed they are part of the receipt and stay as they are.")}
              </p>
              <form onSubmit={submitTransport} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <Field label={t("deliveries.transport.carrier", "Carrier")}>
                  <input value={carrierName} onChange={(e) => setCarrierName(e.target.value)} className="input" style={{ width: "100%" }} />
                </Field>
                <Field label={t("deliveries.transport.vehiclePlate", "Vehicle plate")} required>
                  <input
                    value={vehiclePlate}
                    onChange={(e) => setVehiclePlate(e.target.value)}
                    required
                    className="input"
                    style={{ width: "100%" }}
                  />
                </Field>
                <Field label={t("deliveries.transport.trailerPlate", "Trailer plate")}>
                  <input value={trailerPlate} onChange={(e) => setTrailerPlate(e.target.value)} className="input" style={{ width: "100%" }} />
                </Field>
                <Field label={t("deliveries.transport.driver", "Driver")} required>
                  <input
                    value={driverName}
                    onChange={(e) => setDriverName(e.target.value)}
                    required
                    className="input"
                    style={{ width: "100%" }}
                  />
                </Field>
                <button
                  type="submit"
                  className="btn"
                  disabled={busy || !vehiclePlate.trim() || !driverName.trim()}
                >
                  {t("deliveries.action.saveTransport", "Save transport")}
                </button>
              </form>
            </section>
          )}

          {/* Last of the things that can be done while the truck is only expected: the order can
              also be dropped. Plain styling on purpose — the emphasis belongs to the step forward,
              not to the withdrawal. */}
          {mayCancel && (
            <section style={card}>
              <h2 style={cardTitle}>{t("deliveries.section.cancel", "Withdrawal")}</h2>
              <p style={{ margin: 0, fontSize: 12, color: "var(--ink-3)" }}>
                {t(
                  "deliveries.hint.cancel",
                  "An announcement may be taken back while the truck is still expected. The supplier sees that it was withdrawn, and why.",
                )}
              </p>
              <form onSubmit={doCancel} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <Field label={t("deliveries.field.cancelReason", "Reason")} required>
                  <textarea
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    rows={2}
                    className="input"
                    style={{ resize: "vertical", fontFamily: "inherit", width: "100%" }}
                    placeholder={t(
                      "deliveries.field.cancelReasonPlaceholder",
                      "Why is this announcement being withdrawn?",
                    )}
                  />
                </Field>
                <button type="submit" className="btn" disabled={busy || !cancelReason.trim()}>
                  <Ic.x /> {t("deliveries.action.cancel", "Withdraw the announcement")}
                </button>
              </form>
            </section>
          )}

          {canQuality && delivery.status === "CONFIRMED" && (
            <section style={{ ...card, borderColor: "var(--brand-primary)", boxShadow: "0 0 0 3px var(--brand-primary-50)" }}>
              <h2 style={cardTitle}>{t("deliveries.action.sample", "Sample taken")}</h2>
              <p style={{ margin: 0, fontSize: 12, color: "var(--ink-3)" }}>
                {t("deliveries.hint.sample", "The truck is at the gate and the sample is taken. The fast parameters take about ten minutes, the whole sample about thirty; the load is weighed only once the figures allow it.")}
              </p>
              <button type="button" className="btn primary" onClick={() => void doSample()} disabled={busy}>
                <Ic.check /> {t("deliveries.action.sample", "Sample taken")}
              </button>
            </section>
          )}

          {canConfirm && delivery.status === "SAMPLED" && (
            <section style={{ ...card, borderColor: "var(--brand-primary)", boxShadow: "0 0 0 3px var(--brand-primary-50)" }}>
              <h2 style={cardTitle}>{t("deliveries.action.recordReceipt", "Enter weighbridge receipt")}</h2>
              <form onSubmit={submitReceipt} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <Field label={t("deliveries.field.grossKg", "Gross (kg)")}>
                  <input type="number" min="0" step="0.01" value={grossKg} onChange={(e) => setGrossKg(e.target.value)} required className="input" style={{ width: "100%" }} />
                </Field>
                <Field label={t("deliveries.field.tareKg", "Tare (kg)")}>
                  <input type="number" min="0" step="0.01" value={tareKg} onChange={(e) => setTareKg(e.target.value)} required className="input" style={{ width: "100%" }} />
                </Field>
                <Field label={t("deliveries.field.dispatchNoteQtyKg", "Quantity per supplier dispatch note (kg)")}>
                  <input type="number" min="0" step="0.01" value={dispatchNoteQtyKg} onChange={(e) => setDispatchNoteQtyKg(e.target.value)} className="input" style={{ width: "100%" }} />
                </Field>
                <Field label={t("deliveries.transport.ticketNo", "Weighbridge ticket")}>
                  <input value={ticketNo} onChange={(e) => setTicketNo(e.target.value)} className="input" style={{ width: "100%" }} />
                </Field>
                <Field label={t("deliveries.transport.gateIn", "Gate in")}>
                  <input type="datetime-local" value={gateIn} onChange={(e) => setGateIn(e.target.value)} className="input" style={{ width: "100%" }} />
                </Field>
                <Field label={t("deliveries.transport.gateOut", "Gate out")}>
                  <input type="datetime-local" value={gateOut} onChange={(e) => setGateOut(e.target.value)} className="input" style={{ width: "100%" }} />
                </Field>
                <button type="submit" className="btn primary" disabled={busy}>{t("action.save", "Save")}</button>
              </form>
            </section>
          )}

          {canSettle && delivery.status === "RECEIVED" && (
            <section style={{ ...card, borderColor: "var(--brand-primary)", boxShadow: "0 0 0 3px var(--brand-primary-50)" }}>
              <h2 style={cardTitle}>{t("deliveries.action.settle", "Settlement")}</h2>
              <form onSubmit={submitSettle} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {proposalError && (
                  <Field label={t("deliveries.settlement.manualPrice", "Unit price (per t)")}>
                    <input
                      type="number"
                      step="0.01"
                      value={manualPrice}
                      onChange={(e) => setManualPrice(e.target.value)}
                      className="input"
                      style={{ width: "100%" }}
                    />
                  </Field>
                )}
                {proposalError && (
                  <span style={{ fontSize: 11, color: "var(--ink-3)" }}>
                    {t(
                      "deliveries.settlement.manualPriceHint",
                      "No contract price applies to this delivery — enter the unit price by hand.",
                    )}
                  </span>
                )}

                {proposal && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {proposal.warnings.map((w) => (
                      <span key={`${w.parameterId}-${w.kind}`} style={warnBox}>
                        {w.kind === "BELOW_MIN_ACCEPTED"
                          ? t(
                              "deliveries.settlement.belowMin",
                              "{{measured}} is below the accepted minimum {{limit}}",
                              { measured: w.measured, limit: w.limit },
                            )
                          : t(
                              "deliveries.settlement.aboveMax",
                              "{{measured}} is above the accepted maximum {{limit}}",
                              { measured: w.measured, limit: w.limit },
                            )}
                      </span>
                    ))}
                    <table className="tbl" style={{ fontSize: 12 }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: "left" }}>{t("deliveries.settlement.parameter", "Parameter")}</th>
                          <th style={{ textAlign: "right" }}>{t("deliveries.settlement.measured", "Measured")}</th>
                          <th style={{ textAlign: "right" }}>{t("deliveries.settlement.base", "Base")}</th>
                          <th style={{ textAlign: "right" }}>{t("deliveries.settlement.correction", "Correction")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {proposal.lines.map((l) => {
                          const par = parameters.find((x) => x.id === l.parameterId);
                          return (
                            <tr key={l.parameterId}>
                              <td>
                                {par
                                  ? localizedName(
                                      { name: par.key, nameI18n: par.nameI18n },
                                      i18n.resolvedLanguage ?? "sr",
                                    )
                                  : l.parameterId}
                              </td>
                              <td style={{ textAlign: "right" }}>{l.measured}</td>
                              <td style={{ textAlign: "right" }}>{l.baseValue}</td>
                              <td style={{ textAlign: "right" }}>{l.correctionPct} %</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <span style={{ fontSize: 12, color: "var(--ink-3)" }}>
                      {t("deliveries.settlement.proposed", "Proposed")}:{" "}
                      <b>
                        {proposal.amount} {proposal.currency}
                      </b>
                    </span>
                  </div>
                )}

                <Field label={t("deliveries.field.priceCorrectionPct", "Price correction (%)")}>
                  <input type="number" step="0.01" value={priceCorrectionPct} onChange={(e) => setPriceCorrectionPct(e.target.value)} required className="input" style={{ width: "100%" }} />
                </Field>
                <Field label={t("deliveries.field.settledAmount", "Settled amount")}>
                  <input type="number" step="0.01" value={settledAmount} onChange={(e) => setSettledAmount(e.target.value)} required className="input" style={{ width: "100%" }} />
                </Field>
                <Field label={t("deliveries.settlement.overrideReason", "Reason for deviation")}>
                  <input
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    disabled={!deviates}
                    placeholder={
                      deviates
                        ? t("deliveries.settlement.overrideRequired", "Required — the amount differs from the proposal")
                        : t("deliveries.settlement.overrideNotNeeded", "Not needed — the amount matches the proposal")
                    }
                    className="input"
                    style={{ width: "100%" }}
                  />
                </Field>
                <button type="submit" className="btn primary" disabled={busy || (deviates && !overrideReason.trim())}>
                  {t("deliveries.action.settle", "Settlement")}
                </button>
              </form>
            </section>
          )}

          {labMayEnter && (
            <section style={{ ...card, borderColor: "var(--brand-primary)", boxShadow: "0 0 0 3px var(--brand-primary-50)" }}>
              <h2 style={cardTitle}>{t("deliveries.action.recordQuality", "Enter quality analysis")}</h2>
              <form onSubmit={submitQuality} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {parameters.length === 0 && (
                  <p style={{ margin: 0, fontSize: 12, color: "var(--ink-3)" }}>
                    {t("deliveries.quality.noParameters", "No quality parameters are defined yet.")}
                  </p>
                )}
                {parameters.map((p) => {
                  const type = p.resultType ?? "NUMERIC";
                  const recorded = delivery.qualityValues.find((q) => q.parameterId === p.id);
                  const label =
                    localizedName({ name: p.key, nameI18n: p.nameI18n }, i18n.resolvedLanguage ?? "sr")
                    + (p.unit ? ` (${p.unit})` : "");
                  return (
                    <div key={p.id} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <Field label={label}>
                        {type === "BOOLEAN" ? (
                          <Select<string>
                            value={qualityInput[p.id] ?? ""}
                            onChange={(v) => setQualityInput((prev) => ({ ...prev, [p.id]: v }))}
                            ariaLabel={label}
                            style={{ width: "100%" }}
                            options={[
                              { value: "", label: t("deliveries.quality.notMeasured", "— Not measured —") },
                              { value: "true", label: t("common.yes", "Yes") },
                              { value: "false", label: t("common.no", "No") },
                            ]}
                          />
                        ) : type === "TEXT" ? (
                          <textarea
                            rows={2}
                            value={qualityInput[p.id] ?? ""}
                            onChange={(e) => setQualityInput((prev) => ({ ...prev, [p.id]: e.target.value }))}
                            className="input"
                            style={{ width: "100%", resize: "vertical", fontFamily: "inherit" }}
                          />
                        ) : (
                          <input
                            type="number"
                            step={p.decimals > 0 ? `0.${"0".repeat(p.decimals - 1)}1` : "1"}
                            value={qualityInput[p.id] ?? ""}
                            onChange={(e) => setQualityInput((prev) => ({ ...prev, [p.id]: e.target.value }))}
                            className="input"
                            style={{ width: "100%" }}
                          />
                        )}
                      </Field>
                      {recorded && (qualityInput[p.id] ?? "").trim() !== "" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 6, paddingLeft: 10, borderLeft: "2px solid var(--warn)" }}>
                          <span style={{ fontSize: 11.5, color: "var(--ink-3)" }}>
                            {t("deliveries.quality.correctingHint", "This replaces {{current}} — say what happened and why the new finding is right. The head of the laboratory has to sign it.", { current: displayFinding(recorded) })}
                          </span>
                          <Field label={t("deliveries.quality.correctionReason", "Reason")} required>
                            <input
                              value={correctionReason[p.id] ?? ""}
                              onChange={(e) => setCorrectionReason((prev) => ({ ...prev, [p.id]: e.target.value }))}
                              className="input"
                              style={{ width: "100%" }}
                            />
                          </Field>
                          <Field label={t("deliveries.quality.correctionExplanation", "Explanation")} required>
                            <input
                              value={correctionExplanation[p.id] ?? ""}
                              onChange={(e) => setCorrectionExplanation((prev) => ({ ...prev, [p.id]: e.target.value }))}
                              className="input"
                              style={{ width: "100%" }}
                            />
                          </Field>
                        </div>
                      )}
                    </div>
                  );
                })}
                <button type="submit" className="btn primary" disabled={busy}>{t("action.save", "Save")}</button>
              </form>
            </section>
          )}

          {/* Step one, the laboratory's: only a sampled load can be refused, because
              before the sample there is nothing to find. */}
          {canLabReject && delivery.status === "SAMPLED" && (
            <section style={card}>
              <h2 style={cardTitle}>{t("deliveries.action.labReject", "Record non-conformity")}</h2>
              {!showRejectForm ? (
                <>
                  <p style={{ margin: 0, fontSize: 12, color: "var(--ink-3)" }}>
                    {t(
                      "deliveries.hint.labReject",
                      "The laboratory records the finding and stops here. The buy-in desk then confirms the refusal, or a director accepts the load in spite of it.",
                    )}
                  </p>
                  <button
                    type="button"
                    className="btn"
                    style={{ borderColor: "var(--warn)", color: "var(--warn)", background: "var(--surface)" }}
                    onClick={() => setShowRejectForm(true)}
                  >
                    <Ic.x /> {t("deliveries.action.labReject", "Record non-conformity")}
                  </button>
                </>
              ) : (
                <form onSubmit={submitLabReject} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <Field label={t("deliveries.field.labRejectReason", "What was found")} required>
                    <textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      rows={3}
                      className="input"
                      style={{ resize: "vertical", fontFamily: "inherit", width: "100%" }}
                      placeholder={t(
                        "deliveries.field.labRejectReasonPlaceholder",
                        "Which parameter departed, and by how much",
                      )}
                    />
                  </Field>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button type="button" className="btn" onClick={() => setShowRejectForm(false)}>
                      {t("action.cancel", "Cancel")}
                    </button>
                    <button
                      type="submit"
                      className="btn"
                      style={{ borderColor: "var(--warn)", color: "var(--warn)", background: "var(--surface)" }}
                      disabled={busy || !rejectReason.trim()}
                    >
                      {t("deliveries.action.labReject", "Record non-conformity")}
                    </button>
                  </div>
                </form>
              )}
            </section>
          )}

          {/* Step two, and the waiver beside it. Both act on the laboratory's finding, so
              both appear only once there is one. */}
          {delivery.status === "LAB_REJECTED" && (canConfirmRejection || canWaiveRejection) && (
            <section style={{ ...card, borderColor: "var(--warn)" }}>
              <h2 style={cardTitle}>{t("deliveries.section.refusalDecision", "Decision on the refusal")}</h2>
              <p style={{ margin: 0, fontSize: 12, color: "var(--ink-2)" }}>
                {delivery.labRejectReason}
              </p>
              <p style={{ margin: 0, fontSize: 11.5, color: "var(--ink-3)" }}>
                {t(
                  "deliveries.hint.refusalDecision",
                  "The person who recorded the finding cannot decide on it. Either the refusal is confirmed and the supplier told, or a director accepts the load in spite of the finding and it returns to the weighbridge.",
                )}
              </p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {canConfirmRejection && (
                  <button
                    type="button"
                    className="btn"
                    style={{ borderColor: "var(--err)", color: "var(--err)", background: "var(--surface)" }}
                    disabled={busy}
                    onClick={() => void confirmRejection()}
                  >
                    <Ic.x /> {t("deliveries.action.confirmRejection", "Confirm the refusal")}
                  </button>
                )}
                {canWaiveRejection && !showExceptionForm && (
                  <button type="button" className="btn" disabled={busy} onClick={() => setShowExceptionForm(true)}>
                    {t("deliveries.action.rejectException", "Accept anyway (director)")}
                  </button>
                )}
              </div>
              {canWaiveRejection && showExceptionForm && (
                <form onSubmit={submitRejectException} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <Field label={t("deliveries.field.rejectExceptionReason", "Why the load is accepted anyway")} required>
                    <textarea
                      value={exceptionReason}
                      onChange={(e) => setExceptionReason(e.target.value)}
                      rows={3}
                      className="input"
                      style={{ resize: "vertical", fontFamily: "inherit", width: "100%" }}
                    />
                  </Field>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button type="button" className="btn" onClick={() => setShowExceptionForm(false)}>
                      {t("action.cancel", "Cancel")}
                    </button>
                    <button type="submit" className="btn primary" disabled={busy || !exceptionReason.trim()}>
                      {t("deliveries.action.rejectException", "Accept anyway (director)")}
                    </button>
                  </div>
                </form>
              )}
            </section>
          )}
        </aside>
      </div>

      {id && (delivery.status === "RECEIVED" || delivery.status === "SETTLED") && (
        <ExportButton
          path={`/deliveries/${id}/receipt.pdf`}
          label={t("deliveries.receipt.download", "Receipt (PDF)")}
          filename={`prijemnica-${id}.pdf`}
        />
      )}

      {id && hasPermission("section:delivery_documents:read") && <DeliveryDocuments deliveryId={id} deskMayAct={mayAct} />}

      <style>{`
        ol.wf{list-style:none;padding:0;margin:0 0 12px}
        ol.wf li{position:relative;padding:0 0 18px 26px;color:var(--ink-4);font-size:13px}
        ol.wf li:last-child{padding-bottom:0}
        ol.wf li:not(:last-child):after{content:"";position:absolute;left:6px;top:16px;bottom:0;width:2px;background:var(--border)}
        ol.wf li.wf-done:not(:last-child):after{background:var(--brand-primary)}
        ol.wf li:before{content:"";position:absolute;left:0;top:2px;width:14px;height:14px;border-radius:50%;border:2px solid var(--border);background:var(--surface);box-sizing:border-box}
        ol.wf li.wf-done{color:var(--ink-2)}
        ol.wf li.wf-done:before{background:var(--brand-primary);border-color:var(--brand-primary)}
        ol.wf li.wf-current{color:var(--ink);font-weight:600}
        ol.wf li.wf-current:before{border-color:var(--brand-primary);box-shadow:0 0 0 3px var(--brand-primary-50)}
        ol.wf li.wf-stopped{color:var(--err);font-weight:600}
        ol.wf li.wf-stopped:before{border-color:var(--err);background:var(--err);box-shadow:0 0 0 3px var(--err-bg)}
        ol.wf li.wf-withdrawn{color:var(--ink-3);font-weight:600}
        ol.wf li.wf-withdrawn:before{border-color:var(--ink-4);background:var(--surface-muted);box-shadow:0 0 0 3px var(--surface-muted)}
        ol.wf li.wf-unknown{color:var(--ink-3);font-style:italic}
        ol.wf li.wf-unknown:before{border-style:dashed;border-color:var(--ink-4)}
      `}</style>
    </div>
  );
}

/**
 * Where the delivery stands on the happy path, derived from the status alone — the backend
 * exposes no per-transition history for a delivery.
 *
 * Neither side-exit is a step on that path, so each is drawn as a stop, and the two stops do not
 * look alike. A refusal stops the load where the laboratory found it, which is at the sample, and
 * reads as the error it is. A withdrawal stops it at the announcement — the document that was taken
 * back — and reads neutrally, because nobody found anything wrong with the goods; and it stops
 * there rather than at the slot's confirmation because no confirmation stamp survives to say
 * whether the slot had been confirmed first.
 */
function WorkflowStepper({ delivery, settlementUnknown }: { delivery: Delivery; settlementUnknown: boolean }) {
  const { t } = useTranslation();
  // A receipt imported from the ERP was never announced, confirmed or sampled here. The import found
  // it received, or received and invoiced, and only that is drawn: steps nobody took are not done.
  // A receipt from before 2018 without an invoice is not waiting for its settlement either: whether
  // it had one is not known, and the last step says so rather than standing open.
  if (delivery.origin === "ERP") {
    const settled = delivery.status === "SETTLED";
    if (settlementUnknown) {
      return (
        <ol className="wf">
          <li className="wf-done">{t("deliveries.timeline.step.received", "Received")}</li>
          <li className="wf-unknown">{t("deliveries.settlement.unknownBefore2018", "Settlement unknown (before 2018)")}</li>
        </ol>
      );
    }
    return (
      <ol className="wf">
        <li className={settled ? "wf-done" : "wf-current"}>{t("deliveries.timeline.step.received", "Received")}</li>
        <li className={settled ? "wf-current" : "wf-upcoming"}>{t("deliveries.timeline.step.settled", "Settled")}</li>
      </ol>
    );
  }
  const refused = delivery.status === "REJECTED" || delivery.status === "LAB_REJECTED";
  const withdrawn = delivery.status === "CANCELLED";
  const reached = refused
    ? WF_STEPS.indexOf("SAMPLED")
    : withdrawn
      ? WF_STEPS.indexOf("ANNOUNCED")
      : WF_STEPS.indexOf(delivery.status);
  const here = refused ? "stopped" : withdrawn ? "withdrawn" : "current";
  const labels = [
    t("deliveries.timeline.step.announced", "Announced"),
    t("deliveries.timeline.step.confirmed", "Confirmed"),
    t("deliveries.timeline.step.sampled", "Sampled"),
    t("deliveries.timeline.step.received", "Received"),
    t("deliveries.timeline.step.settled", "Settled"),
  ];
  return (
    <ol className="wf">
      {labels.map((label, i) => {
        const state = i < reached ? "done" : i === reached ? here : "upcoming";
        return <li key={label} className={`wf-${state}`}>{label}</li>;
      })}
    </ol>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginTop: 4 }}>{children}</div>;
}

function ReadField({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={fieldLabel}>{label}</span>
      <span style={{ fontSize: 13, color: "var(--ink)", fontFamily: mono ? "var(--mono)" : undefined }}>{value}</span>
    </div>
  );
}

/**
 * The class as assigned, with what it means.
 *
 * The silo and the permitted use are shown and not just the code, because that is what the
 * class decides. The price flag is deliberately absent here: whether a class bears on the
 * price is unsettled, and putting it on the delivery screen would read as though it did.
 */
function DeliveryClassReadout({
  classes,
  provisionalClassId,
  finalClassId,
  classChangeReason,
}: {
  classes: QualityClass[];
  provisionalClassId: string | null;
  finalClassId: string | null;
  classChangeReason: string | null;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "sr";
  const byId = (id: string | null) => (id == null ? null : classes.find((c) => c.id === id) ?? null);
  const provisional = byId(provisionalClassId);
  const settled = byId(finalClassId);
  const shown = settled ?? provisional;

  if (!provisionalClassId && !finalClassId) {
    return (
      <p className="muted" style={{ margin: 0, fontSize: 12 }}>
        {t("deliveries.class.none", "Not classed yet.")}
      </p>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        {provisional && (
          <span className="tag neutral">
            {t("deliveries.class.provisionalShort", "Provisional")}: {provisional.code}
          </span>
        )}
        {settled && (
          <span className="tag ok">
            {t("deliveries.class.finalShort", "Final")}: {settled.code}
          </span>
        )}
        {classChangeReason && (
          <span className="tag warn">{t("deliveries.class.changed", "Class changed")}</span>
        )}
      </div>
      {classChangeReason && (
        <p style={{ margin: 0, fontSize: 12.5, color: "var(--ink-2)" }}>{classChangeReason}</p>
      )}
      {shown && (
        <Grid>
          <ReadField
            label={t("deliveries.class.unloadingPlace", "Unloaded at")}
            value={shown.unloadingPlace ?? "—"}
          />
          <ReadField
            label={t("deliveries.class.usedFor", "May be used for")}
            value={shown.usedFor ?? "—"}
          />
        </Grid>
      )}
      {shown && shown.criteria.length > 0 && (
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 4 }}>
          {shown.criteria.map((cr) => (
            <li key={cr.id} className="muted" style={{ fontSize: 12 }}>
              {criterionText(
                cr,
                localizedName({ name: cr.parameterKey ?? "", nameI18n: cr.parameterNameI18n }, locale),
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Whether the finding sits inside its limit, outside it, or was never judged.
 *
 * Null is the third answer and the reason this exists: with no limit on file nothing
 * judged the finding, and saying „within limits" would be a claim nobody made.
 */
function FindingLimitTag({ outOfRange }: { outOfRange: boolean | null }) {
  const { t } = useTranslation();
  if (outOfRange === true) {
    return <span className="tag err">{t("deliveries.quality.outOfRange", "Outside the limit")}</span>;
  }
  if (outOfRange === false) {
    return <span className="tag ok">{t("deliveries.quality.inRange", "Within the limit")}</span>;
  }
  return <span className="tag neutral">{t("deliveries.quality.notJudged", "Not judged")}</span>;
}

/** Whether the finding still needs the head of the laboratory. */
function FindingStatusTag({
  status,
  verifiedByName,
}: {
  status: QualityFindingStatus | null;
  verifiedByName: string | null;
}) {
  const { t } = useTranslation();
  if (status === "PENDING_VERIFICATION") {
    return (
      <span className="tag warn">
        {t("deliveries.quality.pendingVerification", "Awaiting the head of the laboratory")}
      </span>
    );
  }
  if (status === "VERIFIED") {
    return (
      <span className="tag ok">
        {verifiedByName
          ? t("deliveries.quality.verifiedBy", "Verified: {{name}}", { name: verifiedByName })
          : t("deliveries.quality.verified", "Verified")}
      </span>
    );
  }
  return null;
}

/** The refusals this screen can provoke, in the reader's language. */
function deliveryErrorMessage(e: ApiError, t: (key: string, fallback: string) => string): string {
  switch (e.code) {
    case "DELIVERY_WITHOUT_CONTRACT_APPROVAL_REQUIRED":
      return t("deliveries.error.withoutContractApprovalRequired",
          "This delivery runs outside any procurement contract — only a director may confirm it.");
    case "VEHICLE_AND_DRIVER_REQUIRED":
      return t("deliveries.error.vehicleAndDriverRequired",
          "The vehicle plate and the driver are required on an intake order.");
    case "TRANSPORT_LOCKED":
      return t("deliveries.error.transportLocked",
          "The truck has been weighed — its transport details are part of the receipt now.");
    case "CORRECTION_REASON_REQUIRED":
      return t("deliveries.error.correctionReasonRequired",
          "Correcting a recorded finding needs both a reason and an explanation.");
    case "VALUE_TYPE_MISMATCH":
      return t("deliveries.error.valueTypeMismatch",
          "That test does not take that kind of result — the parameter decides whether it is a figure, a yes/no or a description.");
    case "QUALITY_VERIFY_SELF":
      return t("deliveries.error.qualityVerifySelf",
          "A finding is verified by someone other than the person who recorded it.");
    case "QUALITY_VERIFICATION_PENDING":
      return t("deliveries.error.qualityVerificationPending",
          "Some findings still await the head of the laboratory, so this delivery cannot be settled yet.");
    case "ALREADY_VERIFIED":
      return t("deliveries.error.alreadyVerified", "This finding is already verified.");
    case "CLASS_CHANGE_REASON_REQUIRED":
      return t("deliveries.error.classChangeReasonRequired",
          "The final class differs from the provisional one — say why the load changed class.");
    case "CLASS_CULTURE_MISMATCH":
      return t("deliveries.error.classCultureMismatch", "That class belongs to another crop.");
    case "UNKNOWN_CLASS":
      return t("deliveries.error.unknownClass", "That class does not exist.");
    case "REJECT_REASON_REQUIRED":
      return t("deliveries.error.rejectReasonRequired", "Say what the laboratory found.");
    case "REJECT_EXCEPTION_REASON_REQUIRED":
      return t("deliveries.error.rejectExceptionReasonRequired",
          "Say why the load is accepted in spite of the finding.");
    case "DELIVERY_REJECT_SELF":
      return t("deliveries.error.rejectSelf",
          "The refusal is decided by someone other than the person who recorded the finding.");
    case "CANCEL_REASON_REQUIRED":
      return t("deliveries.error.cancelReasonRequired",
          "An announcement is withdrawn with a reason, never without one.");
    case "DELIVERY_ALREADY_WEIGHED":
      return t("deliveries.error.alreadyWeighed",
          "The truck has been weighed, so this announcement can no longer be withdrawn.");
    case "INVALID_TRANSITION":
      return t("deliveries.error.invalidTransition",
          "The delivery has changed state in the meantime — reload the page to see where it stands.");
    default:
      return e.message;
  }
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
      <span style={fieldLabel}>
        {label} {required && <span style={{ color: "var(--err)" }}>*</span>}
      </span>
      {children}
    </label>
  );
}

function fmt(n: number | null): string {
  return n == null ? "—" : String(n);
}

const card: CSSProperties = {
  background: "var(--surface)", border: "1px solid var(--border)",
  borderRadius: "var(--r-md)", padding: 18,
  display: "flex", flexDirection: "column", gap: 8,
};
const cardTitle: CSSProperties = { margin: 0, fontSize: 14, color: "var(--ink)" };
const settlementNote: CSSProperties = { margin: 0, fontSize: 12, color: "var(--ink-3)" };
const fieldLabel: CSSProperties = {
  display: "block", fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4,
};
const kpiStrip: CSSProperties = {
  display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10,
};
const kpiTile: CSSProperties = {
  background: "var(--surface)", border: "1px solid var(--border)",
  borderRadius: "var(--r-md)", padding: "12px 14px",
  display: "flex", flexDirection: "column", gap: 4,
};
const kpiLabel: CSSProperties = {
  fontSize: 10, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--ink-3)",
};
const kpiValueLg: CSSProperties = { fontSize: 18, color: "var(--ink)", display: "flex", alignItems: "center", gap: 6 };
const warnBox: CSSProperties = {
  fontSize: 12,
  color: "var(--warn)",
  background: "var(--warn-bg)",
  border: "1px solid var(--warn)",
  borderRadius: "var(--r-sm)",
  padding: "6px 8px",
};
const crumbMuted: CSSProperties = { color: "var(--ink-4)" };
const crumbLink: CSSProperties = { color: "var(--ink-3)", textDecoration: "none" };
const crumbCurrent: CSSProperties = { color: "var(--ink-2)", fontWeight: 600 };
